<template>
  <!-- Same ghl-ui structure (and default width) as DeleteConfirmationModal, so both look alike -->
  <UIModal :show="show" @update:show="onUpdateShow">
    <template #header>
      <UIModalHeader
        id="rename-linked-workflow-header"
        class="rename-workflow-modal__header"
        :title="t('common.renameWorkflow.title')"
        :description="t('common.renameWorkflow.description')"
        @close="cancel"
      />
    </template>
    <UIModalContent>
      <UIForm id="rename-linked-workflow-form" class="rename-workflow-modal__body" @submit.prevent>
        <UIFormItem
          :label="t('common.renameWorkflow.nameLabel')"
          :validation-status="showRequired ? 'error' : undefined"
          :feedback="showRequired ? t('common.renameWorkflow.nameRequired') : undefined"
        >
          <UIInput
            id="rename-linked-workflow-input"
            v-model="name"
            size="large"
            :placeholder="t('common.renameWorkflow.namePlaceholder')"
            @update:model-value="touched = true"
            @keydown.enter.prevent="submit"
          />
        </UIFormItem>
      </UIForm>
    </UIModalContent>
    <template #footer>
      <UIModalFooter
        id="rename-linked-workflow-footer"
        :positive-text="t('common.save')"
        :negative-text="t('common.cancel')"
        :disabled="!canSave"
        @positive-click="submit"
        @negative-click="cancel"
      />
    </template>
  </UIModal>
</template>

<script setup lang="ts">
import type { LinkedWorkflow } from "@/types/linkedWorkflows";
import {
  UIForm,
  UIFormItem,
  UIInput,
  UIModal,
  UIModalContent,
  UIModalFooter,
  UIModalHeader,
} from "@gohighlevel/ghl-ui";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  show: boolean;
  workflow: LinkedWorkflow | null;
}>();

const emit = defineEmits<{
  rename: [name: string];
  cancel: [];
}>();

const { t } = useI18n();

const name = ref("");
const touched = ref(false);

const trimmedName = computed(() => name.value.trim());
const showRequired = computed(() => touched.value && trimmedName.value === "");
const canSave = computed(
  () => trimmedName.value !== "" && trimmedName.value !== (props.workflow?.name ?? "")
);

watch(
  () => [props.show, props.workflow?.id] as const,
  ([show]) => {
    if (!show) return;
    name.value = props.workflow?.name ?? "";
    touched.value = false;
  },
  { immediate: true }
);

const submit = () => {
  if (!canSave.value) return;
  emit("rename", trimmedName.value);
};

const cancel = () => {
  emit("cancel");
};

const onUpdateShow = (value: boolean) => {
  if (!value) cancel();
};
</script>

<style scoped>
/* Reference: workflow-delete-modal-target.png -- 18px semibold title, 16px gray-900 body */
.rename-workflow-modal__header :deep(.title) {
  color: var(--gray-900, #101828);
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
}
.rename-workflow-modal__header :deep(.description) {
  margin-top: 16px;
  color: var(--gray-900, #101828);
  font-size: 16px;
  line-height: 24px;
}
/* ~8px label-to-input; the large input is ~40px tall and full width */
.rename-workflow-modal__body :deep(.n-form-item-label) {
  min-height: 0;
  padding-bottom: 8px;
  color: var(--gray-900, #101828);
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
}
.rename-workflow-modal__body :deep(.n-form-item-feedback-wrapper) {
  min-height: 0;
  padding-top: 4px;
}
.rename-workflow-modal__body :deep(.n-input) {
  width: 100%;
  font-size: 16px;
}
</style>
